import React from 'react'
import {defineMessages, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {GetTheAppButtons} from 'components/GetTheAppButtons/GetTheAppButtons'
import {getLanguageCode} from 'modules/meta/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {PageWidthContainer} from '@vio/atlas/helpers/PageWidthContainer'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import minus from '@vio/atlas/utils/minus'
import {Placement} from '@vio/core/src/deepLinking/types'

import {AppRatingsAndReviews} from '../AppRatingsAndReviews'
import {HighlightedTitle} from '../HighlightedTitle'

const Container = styled.div`
  overflow: hidden;
  margin-top: ${minus(cssTheme.layout.spacing.s700)};

  ${linariaMq.desktopXs} {
    margin-top: 0;
    padding-top: ${cssTheme.layout.spacing.s700};
  }
`

const SectionWrapper = styled.div`
  background: ${cssTheme.colors.background.brand.inverse};
`

const GetAppWrapper = styled.div`
  padding: 184px 0;

  ${linariaMq.desktopXs} {
    padding: 130px 0;
    min-width: 447px;
  }
`

const Title = styled(Text)`
  color: ${cssTheme.colors.background.neutral.primary};
  margin-bottom: ${cssTheme.layout.spacing.s500};

  ${linariaMq.desktopXs} {
    margin-bottom: ${cssTheme.layout.spacing.s600};
  }
`

const AdvantageItemText = styled(Text)`
  color: ${cssTheme.colors.background.neutral.primary};
`

const AppStoreWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${cssTheme.layout.spacing.s700};
  color: ${cssTheme.colors.background.neutral.primary};
  margin-top: ${cssTheme.layout.spacing.s700};
  ${linariaMq.desktopXs} {
    margin-top: 120px;
  }
`

const PrintScreensLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: ${cssTheme.layout.spacing.s700};
  > div:nth-child(1) {
    margin-top: ${minus(cssTheme.layout.spacing.s700)};
    height: 517px;
  }

  > div:nth-child(2) {
    height: 280px;
  }
`

const PrintScreensRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: ${cssTheme.layout.spacing.s700};
  > div:nth-child(1) {
    margin-top: ${cssTheme.layout.spacing.s900};
    height: 517px;
  }

  > div:nth-child(2) {
    height: 200px;
  }
`

const PrintScreen = styled.div<{src: string}>`
  background-image: ${({src}) => `url(${src})`};
  background-size: contain;
  background-repeat: no-repeat;
  background-position: bottom;
  margin: 0 auto;
  width: 257px;
`

const advantages = defineMessages({
  saveUpToSixty: {
    id: 'homePage.appAdvantagesSaveUpTo',
    defaultMessage: 'Save up to 60%'
  },
  manageYourReservation: {
    id: 'homePage.appAdvantagesManageBookings',
    defaultMessage: 'Manage your bookings'
  },
  trackPrices: {
    id: 'homePage.trackPrices',
    defaultMessage: 'Track prices'
  }
})

const appAdvantageItems = [
  advantages.saveUpToSixty,
  advantages.manageYourReservation,
  advantages.trackPrices
]

export const MobileAppSection = () => {
  const {isMobile} = useDeviceLayout()
  const {formatMessage} = useIntl()
  const languageCode = useSelector(getLanguageCode)

  return (
    <Container>
      <SectionWrapper>
        <PageWidthContainer>
          <Box
            paddingLeft={isMobile ? 's600' : 's950'}
            paddingRight={isMobile ? 's600' : 's950'}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="stretch"
              gap="s600"
            >
              <GetAppWrapper>
                <Title variant={isMobile ? 'title2XL' : 'title4XL'}>
                  <HighlightedTitle source="UnlockExclusiveSavings" />
                </Title>

                <Box display="flex" flexDirection="column" gap="s300">
                  {appAdvantageItems.map((text, index) => (
                    <Box
                      display="flex"
                      alignItems="center"
                      key={index}
                      gap="s300"
                    >
                      <Icon
                        name="Checkmark"
                        colorPath="content.neutral.inverse"
                      />
                      <AdvantageItemText variant={isMobile ? 'bodyM' : 'bodyL'}>
                        {formatMessage(text)}
                      </AdvantageItemText>
                    </Box>
                  ))}
                </Box>

                <Box paddingTop={isMobile ? 's700' : 's900'}>
                  <GetTheAppButtons
                    placement={Placement.HomePageAppSection}
                    redirectLanguage={languageCode}
                  />
                </Box>

                <AppStoreWrapper>
                  <AppRatingsAndReviews
                    variant="AppStore"
                    redirectLanguage={languageCode}
                  />
                  <AppRatingsAndReviews
                    variant="GooglePlay"
                    redirectLanguage={languageCode}
                  />
                </AppStoreWrapper>
              </GetAppWrapper>

              {!isMobile && (
                <Box display="flex" justifyContent="space-between" gap="s700">
                  <PrintScreensLeft>
                    <PrintScreen
                      src={`${process.env.NEXT_PUBLIC_STATIC_ASSETS_ENDPOINT}HomePage/rebrand/${languageCode}/iphone-map.webp?v=2`}
                    />
                    <PrintScreen
                      src={`${process.env.NEXT_PUBLIC_STATIC_ASSETS_ENDPOINT}HomePage/rebrand/${languageCode}/iphone-your-stays.webp?v=2`}
                    />
                  </PrintScreensLeft>
                  <PrintScreensRight>
                    <PrintScreen
                      src={`${process.env.NEXT_PUBLIC_STATIC_ASSETS_ENDPOINT}HomePage/rebrand/${languageCode}/iphone-srp.webp?v=2`}
                    />
                    <PrintScreen
                      src={`${process.env.NEXT_PUBLIC_STATIC_ASSETS_ENDPOINT}HomePage/rebrand/${languageCode}/iphone-ap.webp?v=2`}
                    />
                  </PrintScreensRight>
                </Box>
              )}
            </Box>
          </Box>
        </PageWidthContainer>
      </SectionWrapper>
    </Container>
  )
}
